import React, { useState, useEffect } from 'react'
import {
  Box,
  VStack,
  HStack,
  Heading,
  Text,
  Input,
  Button,
  Field,
  Icon,
  Portal,
  Separator,
  Link
} from '@chakra-ui/react'
import { FiMail, FiLock, FiAlertCircle } from 'react-icons/fi'
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  OAuthProvider
} from 'firebase/auth'
import { auth } from '../config/firebase'
import { TermsOfServiceModal } from './TermsOfServiceModal'

interface AuthModalProps {
  isOpen: boolean
  onClose: () => void
  onAuthSuccess: (email: string, idToken?: string) => void
  mode?: 'signup' | 'login'
}

export const AuthModal: React.FC<AuthModalProps> = ({
  isOpen,
  onClose,
  mode: initialMode = 'signup',
  onAuthSuccess
}) => {
  const [mode, setMode] = useState<'signup' | 'login'>(initialMode)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [name, setName] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [socialLoading, setSocialLoading] = useState<'google' | 'apple' | null>(null)
  const [showTermsModal, setShowTermsModal] = useState(false)
  const [termsAccepted, setTermsAccepted] = useState(false)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    // For signup, show Terms of Service first if not yet accepted
    if (mode === 'signup' && !termsAccepted) {
      // Validate before showing terms
      if (!email || !password) {
        setError('Please fill in all required fields')
        return
      }
      if (password.length < 6) {
        setError('Password must be at least 6 characters')
        return
      }
      if (password !== confirmPassword) {
        setError('Passwords do not match')
        return
      }
      // Show terms modal
      setShowTermsModal(true)
      return
    }

    setLoading(true)

    try {
      // Validate
      if (!email || !password) {
        setError('Please fill in all required fields')
        setLoading(false)
        return
      }

      if (mode === 'signup') {
        if (password.length < 6) {
          setError('Password must be at least 6 characters')
          setLoading(false)
          return
        }
        if (password !== confirmPassword) {
          setError('Passwords do not match')
          setLoading(false)
          return
        }
      }

      // Firebase email/password authentication
      let userCredential
      if (mode === 'signup') {
        userCredential = await createUserWithEmailAndPassword(auth, email, password)
        // Update display name if provided
        if (name && userCredential.user) {
          // Note: displayName update would require additional Firebase setup
          // For now, we'll pass the name to the parent component
        }
      } else {
        userCredential = await signInWithEmailAndPassword(auth, email, password)
      }

      // Get ID token and call success handler
      const idToken = await userCredential.user.getIdToken()
      const userEmail = userCredential.user.email || email
      onAuthSuccess(userEmail, idToken)

    } catch (err: any) {
      setError(err.message || 'An error occurred. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleTermsAccept = async () => {
    setTermsAccepted(true)
    setShowTermsModal(false)
    // After accepting terms, proceed with signup
    const form = document.querySelector('form')
    if (form) {
      form.requestSubmit()
    }
  }

  const switchMode = () => {
    setMode(prev => prev === 'signup' ? 'login' : 'signup')
    setError('')
    setPassword('')
    setConfirmPassword('')
    setTermsAccepted(false)
  }

  const handleGoogleSignIn = async () => {
    setError('')
    setSocialLoading('google')
    try {
      const provider = new GoogleAuthProvider()
      const userCredential = await signInWithPopup(auth, provider)
      const idToken = await userCredential.user.getIdToken()
      const userEmail = userCredential.user.email || ''
      onAuthSuccess(userEmail, idToken)
    } catch (err: any) {
      // User cancelled popup
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        setError('')
      } else {
        setError(err.message || 'Failed to sign in with Google')
      }
    } finally {
      setSocialLoading(null)
    }
  }

  const handleAppleSignIn = async () => {
    setError('')
    setSocialLoading('apple')
    try {
      const provider = new OAuthProvider('apple.com')
      // Request email and name scopes
      provider.addScope('email')
      provider.addScope('name')
      const userCredential = await signInWithPopup(auth, provider)
      const idToken = await userCredential.user.getIdToken()
      const userEmail = userCredential.user.email || ''
      onAuthSuccess(userEmail, idToken)
    } catch (err: any) {
      // User cancelled popup
      if (err.code === 'auth/popup-closed-by-user' || err.code === 'auth/cancelled-popup-request') {
        setError('')
      } else {
        setError(err.message || 'Failed to sign in with Apple')
      }
    } finally {
      setSocialLoading(null)
    }
  }

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <>
      <Portal>
      <Box
        position="fixed"
        inset="0"
        zIndex="modal"
        display="flex"
        alignItems="center"
        justifyContent="center"
        bg="blackAlpha.600"
        onClick={onClose}
      >
        <Box
          as="form"
          onSubmit={handleSubmit}
          bg="white"
          borderRadius="xl"
          shadow="2xl"
          maxW="md"
          w="full"
          mx={4}
          maxH="90vh"
          overflowY="auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <Box p={6} borderBottomWidth="1px" borderColor="gray.200">
            <VStack align="start" gap={2}>
              <HStack justify="space-between" w="full">
                <Heading size="lg">
                  {mode === 'signup' ? 'Create Your Account' : 'Sign In'}
                </Heading>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={onClose}
                  aria-label="Close"
                >
                  ×
                </Button>
              </HStack>
              <Text fontSize="sm" color="gray.600">
                {mode === 'signup'
                  ? 'Create an account to save your profile and generate your Spotlight page'
                  : 'Sign in to continue building your Spotlight page'}
              </Text>
            </VStack>
          </Box>

          {/* Body */}
          <Box p={6}>
              <VStack gap={4} align="stretch">
                {error && (
                  <Box
                    p={3}
                    bg="red.50"
                    borderWidth="1px"
                    borderColor="red.200"
                    borderRadius="md"
                    color="red.700"
                  >
                    <HStack gap={2}>
                      <Icon as={FiAlertCircle} />
                      <Text fontSize="sm">{error}</Text>
                    </HStack>
                  </Box>
                )}

                {/* Social Sign-In Buttons */}
                <VStack gap={3} align="stretch">
                  <Button
                    onClick={handleGoogleSignIn}
                    isLoading={socialLoading === 'google'}
                    loadingText="Signing in..."
                    size="lg"
                    variant="outline"
                    w="full"
                    type="button"
                  >
                    <HStack gap={2}>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.874 2.684-6.615z" fill="#4285F4"/>
                        <path d="M9 18c2.43 0 4.467-.806 5.96-2.184l-2.908-2.258c-.806.54-1.837.86-3.052.86-2.347 0-4.332-1.585-5.043-3.715H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
                        <path d="M3.957 10.703c-.18-.54-.282-1.117-.282-1.703s.102-1.163.282-1.703V4.965H.957C.348 6.175 0 7.55 0 9s.348 2.825.957 4.035l3-2.332z" fill="#FBBC05"/>
                        <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.965L3.957 7.3C4.668 5.17 6.653 3.58 9 3.58z" fill="#EA4335"/>
                      </svg>
                      <Text>Continue with Google</Text>
                    </HStack>
                  </Button>

                  <Button
                    onClick={handleAppleSignIn}
                    isLoading={socialLoading === 'apple'}
                    loadingText="Signing in..."
                    size="lg"
                    variant="outline"
                    w="full"
                    type="button"
                    bg="black"
                    color="white"
                    _hover={{ bg: 'gray.800' }}
                  >
                    <HStack gap={2}>
                      <svg width="18" height="18" viewBox="0 0 18 18" fill="currentColor">
                        <path d="M13.545 2.91c-.78 1.027-2.05 1.827-3.182 1.716-.14-1.39.48-2.72 1.23-3.68.81-1.05 2.12-1.86 3.18-1.72-.12 1.4.45 2.68 1.17 3.68zm1.23 2.25c-1.56-.09-2.88.93-3.6.93-.73 0-1.89-.9-3.12-.87-1.6.03-3.08.93-4.08 2.37-1.74 3.02-.45 7.5 1.23 9.95.84 1.2 1.84 2.55 3.15 2.5 1.26-.06 1.74-.81 3.27-.81 1.53 0 1.95.81 3.28.78 1.35-.03 2.22-1.2 3.06-2.4.96-1.41 1.35-2.78 1.38-2.85-.03-.03-2.64-1.02-2.67-4.05-.03-2.55 2.08-3.77 2.16-3.84-1.17-1.71-3-1.9-3.66-1.95z"/>
                      </svg>
                      <Text>Continue with Apple</Text>
                    </HStack>
                  </Button>
                </VStack>

                <HStack gap={2} align="center">
                  <Separator />
                  <Text fontSize="sm" color="gray.500">or</Text>
                  <Separator />
                </HStack>

                {mode === 'signup' && (
                  <Field.Root>
                    <Field.Label>Full Name</Field.Label>
                    <Input
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      placeholder="Your name"
                      size="lg"
                    />
                  </Field.Root>
                )}

                <Field.Root invalid={!!error && !email} required>
                  <Field.Label>Email Address</Field.Label>
                  <Input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    size="lg"
                  />
                  {error && !email && <Field.ErrorText>{error}</Field.ErrorText>}
                </Field.Root>

                <Field.Root invalid={!!error && !password} required>
                  <Field.Label>Password</Field.Label>
                  <Input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'signup' ? 'At least 6 characters' : 'Your password'}
                    size="lg"
                  />
                  {mode === 'signup' && (
                    <Text fontSize="xs" color="gray.500" mt={1}>
                      Must be at least 6 characters
                    </Text>
                  )}
                  {error && !password && <Field.ErrorText>{error}</Field.ErrorText>}
                </Field.Root>

                {mode === 'signup' && (
                  <Field.Root invalid={!!error && password !== confirmPassword} required>
                    <Field.Label>Confirm Password</Field.Label>
                    <Input
                      type="password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      placeholder="Confirm your password"
                      size="lg"
                    />
                    {password !== confirmPassword && confirmPassword && (
                      <Field.ErrorText>Passwords do not match</Field.ErrorText>
                    )}
                  </Field.Root>
                )}

                <Box h="1px" bg="gray.200" w="full" />

                <VStack gap={2} align="stretch">
                  {mode === 'signup' && (
                    <Text fontSize="xs" color="gray.600" textAlign="center">
                      By creating an account, you agree to our{' '}
                      <Link
                        color="brand.600"
                        fontWeight="medium"
                        onClick={(e) => {
                          e.preventDefault()
                          setShowTermsModal(true)
                        }}
                        cursor="pointer"
                        _hover={{ textDecoration: 'underline' }}
                      >
                        Terms of Service
                      </Link>
                    </Text>
                  )}
                  <Text fontSize="sm" color="gray.600" textAlign="center">
                    {mode === 'signup' ? 'Already have an account?' : "Don't have an account?"}
                  </Text>
                  <Button
                    variant="ghost"
                    onClick={switchMode}
                    size="sm"
                    w="full"
                    type="button"
                  >
                    {mode === 'signup' ? 'Sign In Instead' : 'Create Account Instead'}
                  </Button>
                </VStack>
              </VStack>
          </Box>

          {/* Footer */}
          <Box p={6} borderTopWidth="1px" borderColor="gray.200">
            <HStack gap={3} w="full">
              <Button variant="ghost" onClick={onClose} flex={1} type="button">
                Cancel
              </Button>
              <Button
                type="submit"
                colorPalette="brand"
                flex={2}
                isLoading={loading}
                loadingText={mode === 'signup' ? 'Creating...' : 'Signing in...'}
              >
                {mode === 'signup' ? 'Create Account' : 'Sign In'}
              </Button>
            </HStack>
          </Box>
        </Box>
      </Box>
      </Portal>

      {/* Terms of Service Modal */}
      <TermsOfServiceModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
        showAcceptCheckbox={mode === 'signup'}
      />
    </>
  )
}

